(() => {
  const qs = (el, s) => el.querySelector(s);
  const qsa = (el, s) => Array.from(el.querySelectorAll(s));

  function uniq(arr) {
    const seen = new Set();
    return arr.filter(x => (seen.has(x) ? false : (seen.add(x), true)));
  }

  function clamp(n, a, b) {
    return Math.max(a, Math.min(b, n));
  }

  // Cache images so we don't re-download on every draw
  const imageCache = new Map();
  function loadImage(url) {
  if (!url) return Promise.reject(new Error("Missing image URL"));
  if (imageCache.has(url)) return imageCache.get(url);

  const p = new Promise((resolve, reject) => {
    // Try 1: crossOrigin=anonymous (best practice)
    const img1 = new Image();
    img1.crossOrigin = "anonymous";
    img1.onload = () => resolve(img1);
    img1.onerror = () => {
      // Try 2: no crossOrigin (renders fine; may taint canvas but we don't export)
      const img2 = new Image();
      img2.onload = () => resolve(img2);
      img2.onerror = () => reject(new Error("Image failed to load: " + url));
      img2.src = url;
    };
    img1.src = url;
  });

  imageCache.set(url, p);
  return p;
}

  async function ensureFontLoaded(family, weight, sizePx) {
    try {
      if (document.fonts && document.fonts.load) {
        await document.fonts.load(`${weight || 700} ${sizePx}px ${family}`);
      }
    } catch (_) {}
  }

  function findMaskBBox(maskImg) {
    const c = document.createElement("canvas");
    c.width = maskImg.width;
    c.height = maskImg.height;
    const ctx = c.getContext("2d");
    ctx.drawImage(maskImg, 0, 0);
    const { data, width, height } = ctx.getImageData(0, 0, c.width, c.height);

    let minX = width, minY = height, maxX = -1, maxY = -1;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const a = data[(y * width + x) * 4 + 3];
        if (a > 10) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) return { x: 0, y: 0, w: width, h: height };
    return { x: minX, y: minY, w: (maxX - minX + 1), h: (maxY - minY + 1) };
  }

  function ensurePropInput(form, key) {
    const name = `properties[${key}]`;
    let input = form.querySelector(`input[name="${CSS.escape(name)}"]`);
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      form.appendChild(input);
    }
    return input;
  }

  async function init() {
    const root = document.querySelector("[data-pfc]");
    if (!root) return;

    const configUrl = root.dataset.configUrl;
    const baseUrl = root.dataset.baseImage;
    const maskUrl = root.dataset.maskImage;

    const previewHeight = parseInt(root.dataset.previewHeight || "420", 10);
    const requireName = String(root.dataset.requireName || "true") === "true";
    const maskScalePct = parseFloat(root.dataset.maskScale || "100");
    const maskOffsetX = parseFloat(root.dataset.maskOffsetX || "0");
    const maskOffsetY = parseFloat(root.dataset.maskOffsetY || "0");

    const nameInput = qs(root, "[data-pfc-name]");
    const errorEl = qs(root, "[data-pfc-error]");
    const doodleErrorEl = qs(root, "[data-pfc-doodle-error]");

    const fontsWrap = qs(root, "[data-pfc-fonts]");
    const colorsWrap = qs(root, "[data-pfc-colors]");

    const iconsSection = qs(root, "[data-pfc-icons-section]");
    const doodlesSection = qs(root, "[data-pfc-doodles-section]");
    const iconsTabsWrap = qs(root, "[data-pfc-icons-tabs]");
    const doodlesTabsWrap = qs(root, "[data-pfc-doodles-tabs]");
    const iconsGrid = qs(root, "[data-pfc-icons]");
    const doodlesGrid = qs(root, "[data-pfc-doodles]");

    const canvas = qs(root, "[data-pfc-canvas]");
    const cctx = canvas.getContext("2d");

    // Locate the product form to attach line item properties
    const form =
      root.closest(".product")?.querySelector('form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]');

    // Load config JSON
    let config;
    try {
      const res = await fetch(configUrl, { cache: "no-store" });
      if (!res.ok) throw new Error(`Config fetch failed: ${res.status}`);
      config = await res.json();
    } catch (e) {
      console.error("[PFC] Failed to load config JSON:", e);
      return;
    }

    // Load base and mask images
    let baseImg, maskImg;
    try {
      [baseImg, maskImg] = await Promise.all([loadImage(baseUrl), loadImage(maskUrl)]);
    } catch (e) {
      console.error("[PFC] Failed to load base/mask images:", e);
      return;
    }

    // Canvas uses base native resolution; CSS controls visible height
    canvas.width = baseImg.width;
    canvas.height = baseImg.height;
    canvas.style.width = "100%";
    canvas.style.height = `${previewHeight}px`;

    // Mask bbox and cropped mask region (for proper scaling)
    const maskBBox = findMaskBBox(maskImg);

    const maskCrop = document.createElement("canvas");
    maskCrop.width = maskBBox.w;
    maskCrop.height = maskBBox.h;
    const maskCropCtx = maskCrop.getContext("2d");
    maskCropCtx.drawImage(
      maskImg,
      maskBBox.x, maskBBox.y, maskBBox.w, maskBBox.h,
      0, 0, maskBBox.w, maskBBox.h
    );

    function getTransformedBBox() {
      const s = maskScalePct / 100;
      const cx = maskBBox.x + maskBBox.w / 2;
      const cy = maskBBox.y + maskBBox.h / 2;
      const w = maskBBox.w * s;
      const h = maskBBox.h * s;
      const x = cx - w / 2 + maskOffsetX;
      const y = cy - h / 2 + maskOffsetY;
      return { x, y, w, h };
    }

    // State
    const state = {
      variant: "name_icon",
      name: "",
      fontId: (config.fonts?.[0]?.id || ""),
      colorId: (config.colors?.[0]?.id || ""),
      iconId: "",    // single icon optional
      doodleId: ""   // required in doodle variants
    };

    // UI renderers
    function renderFonts() {
      fontsWrap.innerHTML = "";
      (config.fonts || []).forEach(f => {
        const b = document.createElement("button");
        b.type = "button";
        b.className = "pfc__chip";
        b.textContent = f.label;
        b.dataset.fontId = f.id;
        b.style.fontFamily = `${f.cssFamily}, system-ui, -apple-system, Segoe UI, Arial`;
        b.onclick = () => {
          state.fontId = f.id;
          syncUI();
          draw();
        };
        fontsWrap.appendChild(b);
      });
    }

    function renderColors() {
      colorsWrap.innerHTML = "";
      (config.colors || []).forEach(c => {
        const b = document.createElement("button");
        b.type = "button";
        b.className = "pfc__swatch";
        b.dataset.colorId = c.id;
        b.title = c.label;
        b.style.background = c.hex;
        b.onclick = () => {
          state.colorId = c.id;
          syncUI();
          draw();
        };
        colorsWrap.appendChild(b);
      });
    }

    function renderTabs(wrap, categories, active, onPick) {
      wrap.innerHTML = "";
      const all = ["All", ...categories];
      all.forEach(cat => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "pfc__tab";
        btn.textContent = cat;
        btn.setAttribute("aria-selected", String(cat === active));
        btn.onclick = () => onPick(cat);
        wrap.appendChild(btn);
      });
    }

    function renderAssetGrid(gridEl, items, selectedId, allowNone, onPick) {
      gridEl.innerHTML = "";

      if (allowNone) {
        const none = document.createElement("button");
        none.type = "button";
        none.className = "pfc__asset pfc__asset--none";
        none.textContent = "No icon";
        none.setAttribute("role", "radio");
        none.setAttribute("aria-checked", String(!selectedId));
        none.onclick = () => onPick("");
        gridEl.appendChild(none);
      }

      items.forEach(it => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "pfc__asset";
        btn.setAttribute("role", "radio");
        btn.setAttribute("aria-checked", String(it.id === selectedId));
        btn.title = it.label;

        const img = document.createElement("img");
        img.loading = "lazy";
        img.alt = it.label;
        img.src = it.url;
        btn.appendChild(img);

        btn.onclick = () => onPick(it.id);
        gridEl.appendChild(btn);
      });
    }

    let activeIconCat = "All";
    let activeDoodleCat = "All";

    function renderIcons() {
      const icons = (config.icons || []).filter(x => !!x.url);
      const cats = uniq(icons.map(i => i.category || "Other"));
      renderTabs(iconsTabsWrap, cats, activeIconCat, (cat) => {
        activeIconCat = cat;
        renderIcons();
      });

      const filtered = activeIconCat === "All" ? icons : icons.filter(i => (i.category || "Other") === activeIconCat);
      // Icons are single-select + optional
      renderAssetGrid(iconsGrid, filtered, state.iconId, true, (id) => {
        state.iconId = id;
        syncUI();
        draw();
      });
    }

    function renderDoodles() {
      const doodles = (config.doodles || []).filter(x => !!x.url);
      const cats = uniq(doodles.map(d => d.category || "Other"));
      renderTabs(doodlesTabsWrap, cats, activeDoodleCat, (cat) => {
        activeDoodleCat = cat;
        renderDoodles();
      });

      const filtered = activeDoodleCat === "All" ? doodles : doodles.filter(d => (d.category || "Other") === activeDoodleCat);
      // Doodles are single-select and required in doodle variants (no "none")
      renderAssetGrid(doodlesGrid, filtered, state.doodleId, false, (id) => {
        state.doodleId = id;
        if (doodleErrorEl) doodleErrorEl.hidden = true;
        syncUI();
        draw();
      });
    }

    // Variant radio listeners
    const variantRadios = qsa(root, '[name="pfc_variant"]');
    variantRadios.forEach(r => {
      r.addEventListener("change", () => {
        if (r.checked) {
          state.variant = r.value;
          if (doodleErrorEl) doodleErrorEl.hidden = true;
          syncUI();
          draw();
        }
      });
    });

    // Name input
    nameInput.addEventListener("input", () => {
      state.name = nameInput.value || "";
      if (errorEl) errorEl.hidden = true;
      draw();
    });

    function syncUI() {
      // show/hide pickers based on variant
      const v = state.variant;
      const showIcons = (v === "name_icon" || v === "name_doodle_icon");
      const showDoodles = (v === "name_doodle" || v === "name_doodle_icon");

      if (iconsSection) iconsSection.hidden = !showIcons;
      if (doodlesSection) doodlesSection.hidden = !showDoodles;

      // selection styling
      qsa(fontsWrap, ".pfc__chip").forEach(b => {
        b.setAttribute("aria-pressed", String(b.dataset.fontId === state.fontId));
      });

      qsa(colorsWrap, ".pfc__swatch").forEach(b => {
        b.setAttribute("aria-pressed", String(b.dataset.colorId === state.colorId));
      });

      // refresh grids so aria-checked stays correct
      if (showIcons) renderIcons();
      if (showDoodles) renderDoodles();

      // write line item properties
      if (form) {
        const font = (config.fonts || []).find(f => f.id === state.fontId);
        const color = (config.colors || []).find(c => c.id === state.colorId);
        const icon = (config.icons || []).find(i => i.id === state.iconId);
        const doodle = (config.doodles || []).find(d => d.id === state.doodleId);

        ensurePropInput(form, "Customisation").value =
          v === "name_icon" ? "Name + Icon" :
          v === "name_doodle" ? "Name + Doodle" :
          "Name + Doodle + Icon";

        ensurePropInput(form, "Pet name").value = state.name || "";
        ensurePropInput(form, "Font").value = font?.label || "";
        ensurePropInput(form, "Color").value = color?.label || "";

        // single icon
        ensurePropInput(form, "Icon").value = icon?.label || "";
        ensurePropInput(form, "_pfc_icon_id").value = state.iconId || "";

        ensurePropInput(form, "Doodle").value = doodle?.label || "";
        ensurePropInput(form, "_pfc_doodle_id").value = state.doodleId || "";
      }
    }

    // Validation on Add to cart
    if (form) {
      form.addEventListener("submit", (e) => {
        const nameOk = !requireName || (state.name && state.name.trim().length > 0);
        if (!nameOk) {
          e.preventDefault();
          if (errorEl) errorEl.hidden = false;
          nameInput.focus();
          return;
        }

        const needsDoodle = (state.variant === "name_doodle" || state.variant === "name_doodle_icon");
        if (needsDoodle && !state.doodleId) {
          e.preventDefault();
          if (doodleErrorEl) doodleErrorEl.hidden = false;
          return;
        }
      });
    }
// ================================
// Add-on line item button (paid)
// ================================
const addAddonBtn = qs(root, "[data-pfc-add-addon]");
const addonStatus = qs(root, "[data-pfc-addon-status]");

function setAddonStatus(msg, isError = false) {
  if (!addonStatus) return;
  addonStatus.style.display = "block";
  addonStatus.textContent = msg;
  addonStatus.style.color = isError ? "#b00020" : "rgba(17,17,17,.75)";
}

function getAddonVariantIdForSelection() {
  const vNameIcon = parseInt(root.dataset.addonVariantNameIcon || "", 10);
  const vDoodle = parseInt(root.dataset.addonVariantNameDoodle || "", 10);

  if (state.variant === "name_icon") return vNameIcon;
  if (state.variant === "name_doodle" || state.variant === "name_doodle_icon") return vDoodle;

  return NaN;
}

if (addAddonBtn) {
  addAddonBtn.addEventListener("click", async () => {
    // Validate name
    const nameOk = !requireName || (state.name && state.name.trim().length > 0);
    if (!nameOk) {
      if (errorEl) errorEl.hidden = false;
      nameInput?.focus();
      return;
    }

    // Validate doodle if required
    const needsDoodle = (state.variant === "name_doodle" || state.variant === "name_doodle_icon");
    if (needsDoodle && !state.doodleId) {
      if (doodleErrorEl) doodleErrorEl.hidden = false;
      return;
    }

    const addonVariantId = getAddonVariantIdForSelection();
    if (!addonVariantId || Number.isNaN(addonVariantId)) {
      setAddonStatus("Add-on product is not configured. Please contact support.", true);
      return;
    }

    // Build properties for the add-on line item
    const font = (config.fonts || []).find(f => f.id === state.fontId);
    const color = (config.colors || []).find(c => c.id === state.colorId);
    const icon = (config.icons || []).find(i => i.id === state.iconId);
    const doodle = (config.doodles || []).find(d => d.id === state.doodleId);

    const bundleId = `PFC-${Date.now()}-${Math.random().toString(16).slice(2)}`;

    const properties = {
      "_pfc_bundle_id": bundleId,
      "Customisation":
        state.variant === "name_icon" ? "Name + Icon" :
        state.variant === "name_doodle" ? "Name + Doodle" :
        "Name + Doodle + Icon",
      "Pet name": (state.name || "").trim(),
      "Font": font?.label || "",
      "Font ID": state.fontId || "",
      "Color": color?.label || "",
      "Color HEX": color?.hex || "",
      "Icon": icon?.label || "",
      "Icon ID": state.iconId || "",
      "Doodle": doodle?.label || "",
      "Doodle ID": state.doodleId || ""
    };

    addAddonBtn.disabled = true;
    setAddonStatus("Adding personalisation to cart…");

    try {
      const res = await fetch((window.Shopify?.routes?.root || "/") + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({
        id: addonVariantId,
        quantity: 1,
        properties
       })
      });

      const data = await res.json().catch(() => ({}));

       if (!res.ok) {
        // Shopify typically returns { status: 422, description: "..." }
        const msg = data?.description || data?.message || `Add to cart failed (${res.status})`;
        throw new Error(msg);
      }

      setAddonStatus("Personalisation added. Redirecting to cart…");
      window.location.href = (window.Shopify?.routes?.root || "/") + "cart";
    } catch (e) {
      console.error("[PFC] add-on add failed", e);
      setAddonStatus(String(e?.message || "Could not add personalisation. Please try again."), true);
      addAddonBtn.disabled = false;
    }
  });
}

    async function draw() {
      const font = (config.fonts || []).find(f => f.id === state.fontId);
      const color = (config.colors || []).find(c => c.id === state.colorId);
      const outlineColor = config.outlineColor || "#bfbfbf";

      const bboxT = getTransformedBBox();

      // base
      cctx.clearRect(0, 0, canvas.width, canvas.height);
      cctx.drawImage(baseImg, 0, 0);

      // sticker layer (same size as base)
      const sticker = document.createElement("canvas");
      sticker.width = canvas.width;
      sticker.height = canvas.height;
      const sctx = sticker.getContext("2d");

      // content area inside mask (padding)
      const padX = bboxT.w * 0.06;
      const padY = bboxT.h * 0.10;
      const area = {
        x: bboxT.x + padX,
        y: bboxT.y + padY,
        w: bboxT.w - padX * 2,
        h: bboxT.h - padY * 2
      };

      const name = (state.name || "").trim();
      const v = state.variant;

      function drawOutlinedText(text, x, y, sizePx, family, weight, fill) {
        if (!text) return;

        sctx.font = `${weight || 700} ${sizePx}px ${family}, system-ui, -apple-system, Segoe UI, Arial`;
        sctx.textAlign = "center";
        sctx.textBaseline = "middle";
        sctx.lineJoin = "round";
        sctx.miterLimit = 2;

        sctx.lineWidth = Math.max(3, Math.round(sizePx * 0.12));
        sctx.strokeStyle = outlineColor;
        sctx.strokeText(text, x, y);

        sctx.fillStyle = fill;
        sctx.fillText(text, x, y);
      }

      // font sizing
      let fontSize = clamp(Math.round(area.h * 0.22), 40, 140);
      if (name.length >= 10) fontSize = Math.round(fontSize * 0.88);

      const family = (font?.cssFamily || "Arial");
      const weight = (font?.weight || 700);

      // ensure font loaded before measuring
      await ensureFontLoaded(family, weight, fontSize);

      sctx.font = `${weight} ${fontSize}px ${family}, Arial`;
      let textW = sctx.measureText(name || " ").width;

      // clamp text width
      const maxTextWidth = area.w * 0.78;
      if (textW > maxTextWidth && name.length) {
        const scale = maxTextWidth / textW;
        fontSize = clamp(Math.round(fontSize * scale), 34, 140);
        await ensureFontLoaded(family, weight, fontSize);
        sctx.font = `${weight} ${fontSize}px ${family}, Arial`;
        textW = sctx.measureText(name).width;
      }

      const centerX = area.x + area.w / 2;

      // load doodle/icon if needed
      let doodleImg = null;
      const needsDoodle = (v === "name_doodle" || v === "name_doodle_icon");
      if (needsDoodle && state.doodleId) {
        const doodle = (config.doodles || []).find(d => d.id === state.doodleId);
        if (doodle?.url) { try { doodleImg = await loadImage(doodle.url); } catch {} }
      }

      let iconImg = null;
      const showIcons = (v === "name_icon" || v === "name_doodle_icon") && !!state.iconId;
      if (showIcons) {
        const icon = (config.icons || []).find(i => i.id === state.iconId);
        if (icon?.url) { try { iconImg = await loadImage(icon.url); } catch {} }
      }

      // layout
      let nameY;

      if (doodleImg) {
        const maxDW = area.w * 0.85;
        const maxDH = area.h * 0.78;

        const dScale = Math.min(maxDW / doodleImg.width, maxDH / doodleImg.height);
        const dw = doodleImg.width * dScale;
        const dh = doodleImg.height * dScale;

        const gap = area.h * 0.02;

        // Center the doodle+name group vertically within the sticker area
        const groupH = dh + gap + fontSize;
        const startY = area.y + (area.h - groupH) / 2;

        const dx = centerX - dw / 2;
        const dy = startY;

        sctx.drawImage(doodleImg, dx, dy, dw, dh);

        nameY = dy + dh + gap + fontSize / 2;
      } else {
        nameY = area.y + area.h * 0.50;
      }

      // draw name
      drawOutlinedText(
        name,
        centerX,
        nameY,
        fontSize,
        family,
        weight,
        (color?.hex || "#000")
      );

      // mirrored icons left/right (single icon choice)
      if (iconImg) {
        const iconSize = clamp(Math.round(fontSize * 0.90), 28, 90);
        const gap = clamp(Math.round(fontSize * 0.45), 18, 60);

        const halfText = Math.min(textW / 2, maxTextWidth / 2);

        let leftX = centerX - halfText - gap - iconSize / 2;
        let rightX = centerX + halfText + gap - iconSize / 2;

        // clamp within content area
        const minX = area.x;
        const maxX = area.x + area.w - iconSize;
        leftX = clamp(leftX, minX, maxX);
        rightX = clamp(rightX, minX, maxX);

        const iconY = nameY - iconSize / 2;
        sctx.drawImage(iconImg, leftX, iconY, iconSize, iconSize);
        sctx.drawImage(iconImg, rightX, iconY, iconSize, iconSize);
      }

      // Apply transformed mask to sticker layer (destination-in)
      const maskCanvas = document.createElement("canvas");
      maskCanvas.width = canvas.width;
      maskCanvas.height = canvas.height;
      const mctx = maskCanvas.getContext("2d");
      mctx.clearRect(0, 0, maskCanvas.width, maskCanvas.height);
      mctx.drawImage(maskCrop, bboxT.x, bboxT.y, bboxT.w, bboxT.h);

      sctx.globalCompositeOperation = "destination-in";
      sctx.drawImage(maskCanvas, 0, 0);
      sctx.globalCompositeOperation = "source-over";

      // Composite sticker onto base
      cctx.drawImage(sticker, 0, 0);
    }

    // Reset
    const resetBtn = qs(root, "[data-pfc-reset]");
    if (resetBtn) {
      resetBtn.addEventListener("click", () => {
        state.name = "";
        state.iconId = "";
        state.doodleId = "";
        state.fontId = (config.fonts?.[0]?.id || "");
        state.colorId = (config.colors?.[0]?.id || "");
        nameInput.value = "";
        if (errorEl) errorEl.hidden = true;
        if (doodleErrorEl) doodleErrorEl.hidden = true;
        syncUI();
        draw();
      });
    }

    // Initial render
    renderFonts();
    renderColors();
    syncUI();
    draw();
  }

  document.addEventListener("DOMContentLoaded", init);
})();
